import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import type { JSX } from "react";
import { Icon } from "../ui";
import { useUiStore } from "../../store/uiStore";
import { useRouterContext } from "../../providers/RouterProvider";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useSearch } from "../search/useSearch";
import { SearchResults } from "../search/SearchResults";
import { ErrorBoundary } from "../ErrorBoundary";
import { useMentionSearch } from "../../modules/episodes/hooks/useMentionSearch";
import { MentionPopup, comboItemCount } from "../shared/MentionPopup";
import type { EntitySearchResult } from "../../modules/episodes/types";
import { useEpisodesListQuery } from "../../modules/episodes/queries";
import { selectNeedsReplyEpisodes } from "../../modules/episodes/helpers";
import { NotificationBell } from "./NotificationBell";
import { NeedsReplyPopover } from "./NeedsReplyPopover";

// ─── @-mention state for plain <input> ──────────────────────────────

interface MentionState {
  readonly active: boolean;
  readonly query: string;
  readonly atIndex: number; // position of "@" in input value
  readonly selectedIndex: number;
  readonly activeCategory: string | null;
}

const MENTION_INITIAL: MentionState = {
  active: false,
  query: "",
  atIndex: -1,
  selectedIndex: 0,
  activeCategory: null,
};

interface TrackedMention {
  readonly id: string;
  readonly name: string;
  /** Start index (inclusive) of the display text "@Name" in the input value */
  readonly start: number;
  /** End index (exclusive) */
  readonly end: number;
}

/**
 * Top command bar with centered search input + live results dropdown.
 * Lives in the Tauri titlebar overlay zone — supports window dragging
 * on empty areas via data-tauri-drag-region.
 */
export function CommandBar(): JSX.Element {
  const [isTauri, setIsTauri] = useState(false);
  const toggleAgentPanel = useUiStore((s) => s.toggleAgentPanel);
  const agentPanelVisible = useUiStore((s) => s.agentPanelVisible);
  const toggleStatusBar = useUiStore((s) => s.toggleStatusBar);
  const statusBarVisible = useUiStore((s) => s.statusBarVisible);
  const backendReady = useUiStore((s) => s.backendReady);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  // ─── @-mention tracking ───────────────────────────────────────────
  const [mention, setMention] = useState<MentionState>(MENTION_INITIAL);
  const [trackedMentions, setTrackedMentions] = useState<readonly TrackedMention[]>([]);

  const mentionIds = useMemo(() => trackedMentions.map((m) => m.id), [trackedMentions]);

  const { results: mentionResults, isLoading: mentionLoading } =
    useMentionSearch(mention.query, mention.active, mention.activeCategory ?? undefined);

  const { results, isSearching } = useSearch(query, mentionIds, focused && backendReady && !mention.active);

  useEffect(() => {
    setIsTauri("__TAURI__" in window || "__TAURI_INTERNALS__" in window);
  }, []);

  // Cmd+K / Ctrl+K to focus
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === "Escape" && focused) {
        if (mention.active) {
          setMention(MENTION_INITIAL);
        } else {
          inputRef.current?.blur();
          setQuery("");
          setFocused(false);
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { window.removeEventListener("keydown", handleKeyDown); };
  }, [focused, mention.active]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!focused) return;
    function handleClick(e: MouseEvent): void {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setFocused(false);
        setMention(MENTION_INITIAL);
      }
    }
    document.addEventListener("mousedown", handleClick);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, [focused]);

  // ─── Detect @-mention trigger in input value ──────────────────────
  const detectMention = useCallback((value: string, cursorPos: number) => {
    const textBefore = value.slice(0, cursorPos);
    const lastAt = textBefore.lastIndexOf("@");

    if (lastAt < 0) {
      setMention(MENTION_INITIAL);
      return;
    }

    // @ must be preceded by whitespace or at start
    const charBefore = lastAt > 0 ? textBefore[lastAt - 1] : " ";
    if (charBefore !== " " && charBefore !== "\n" && charBefore !== "\t" && lastAt !== 0) {
      setMention(MENTION_INITIAL);
      return;
    }

    // Check if this @ is inside a tracked mention (already resolved)
    const isInsideMention = trackedMentions.some(
      (m) => lastAt >= m.start && lastAt < m.end,
    );
    if (isInsideMention) {
      setMention(MENTION_INITIAL);
      return;
    }

    const mentionQuery = textBefore.slice(lastAt + 1);
    // Close if query contains a space (user moved on)
    if (mentionQuery.includes(" ")) {
      setMention(MENTION_INITIAL);
      return;
    }

    setMention((prev) => ({
      active: true,
      query: mentionQuery,
      atIndex: lastAt,
      selectedIndex: prev.active ? prev.selectedIndex : 0,
      activeCategory: prev.active ? prev.activeCategory : null,
    }));
  }, [trackedMentions]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    const cursorPos = e.target.selectionStart ?? value.length;
    detectMention(value, cursorPos);
  }, [detectMention]);

  // ─── Mention selection ────────────────────────────────────────────
  const handleMentionSelect = useCallback((item: EntitySearchResult) => {
    if (!item.name) return;
    const displayText = `@${item.name}`;
    const before = query.slice(0, mention.atIndex);
    const after = query.slice(mention.atIndex + 1 + mention.query.length);
    const newQuery = before + displayText + " " + after;

    const newMention: TrackedMention = {
      id: item.id,
      name: item.name,
      start: mention.atIndex,
      end: mention.atIndex + displayText.length,
    };

    // Shift existing mentions that come after the insertion point
    const lengthDiff = (displayText.length + 1) - (1 + mention.query.length);
    const adjusted = trackedMentions.map((m) =>
      m.start > mention.atIndex
        ? { ...m, start: m.start + lengthDiff, end: m.end + lengthDiff }
        : m,
    );

    setTrackedMentions([...adjusted, newMention]);
    setQuery(newQuery);
    setMention(MENTION_INITIAL);

    // Restore focus + cursor
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input) {
        input.focus();
        const pos = newMention.end + 1;
        input.setSelectionRange(pos, pos);
      }
    });
  }, [query, mention, trackedMentions]);

  const handleMentionCategorySelect = useCallback((schemaId: string) => {
    setMention((prev) => ({ ...prev, activeCategory: schemaId, selectedIndex: 0 }));
  }, []);

  const handleMentionCategoryBack = useCallback(() => {
    setMention((prev) => ({ ...prev, activeCategory: null, selectedIndex: 0 }));
  }, []);

  const { navigate } = useRouterContext();
  const runtime = useAppRuntime();

  // ─── Global needs-reply bell — visible from ANY module. Same
  // needs_input-only selection as the Chats-pane bell (they can never
  // disagree): selectNeedsReplyEpisodes over the shared episodes list
  // query, gated on backendReady like the search queries.
  const [bellOpen, setBellOpen] = useState(false);
  const { data: bellEpisodes } = useEpisodesListQuery({ enabled: backendReady });
  const needsReplyItems = useMemo(
    () => selectNeedsReplyEpisodes(bellEpisodes ?? []),
    [bellEpisodes],
  );
  const handleBellSelect = useCallback(
    (episodeId: string) => {
      setBellOpen(false);
      // Quick-switch straight to the unfinished episode in Chats.
      navigate("episodes", "episode", episodeId);
    },
    [navigate],
  );

  const trackedMentionsRef = useRef(trackedMentions);
  trackedMentionsRef.current = trackedMentions;

  const handleAgentSearch = useCallback(
    (q: string) => {
      useUiStore.getState().setPendingAgentQuery(q);
      useUiStore.getState().setPendingAgentMentionIds(trackedMentionsRef.current.map((m) => m.id));
      navigate("episodes");
      setQuery("");
      setFocused(false);
      setTrackedMentions([]);
      inputRef.current?.blur();
    },
    [navigate],
  );

  // ─── Keyboard handling for mention popup ──────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!mention.active) {
      if (e.key === "Enter") {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const currentQuery = inputRef.current?.value?.trim() ?? "";
        if (currentQuery.length > 0) {
          e.preventDefault();
          handleAgentSearch(currentQuery);
        }
      }
      return;
    }

    const hasQuery = mention.query.length > 0;
    const isComboMode = !hasQuery && !mention.activeCategory;
    const totalItems = isComboMode
      ? comboItemCount(mentionResults.length)
      : mentionResults.length;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setMention((prev) => ({
          ...prev,
          selectedIndex: (prev.selectedIndex + 1) % Math.max(totalItems, 1),
        }));
        break;

      case "ArrowUp":
        e.preventDefault();
        setMention((prev) => ({
          ...prev,
          selectedIndex: (prev.selectedIndex - 1 + Math.max(totalItems, 1)) % Math.max(totalItems, 1),
        }));
        break;

      case "Enter": {
        e.preventDefault();
        if (isComboMode) {
          const recentCount = Math.min(mentionResults.length, 4);
          if (mention.selectedIndex < recentCount) {
            const item = mentionResults[mention.selectedIndex];
            if (item) handleMentionSelect(item);
          } else {
            const catIndex = mention.selectedIndex - recentCount;
            const categories = [
              "contacts.person", "email.message", "projects.task",
              "meetings.calendar_event", "telegram.message", "companies.company",
              "episodes.episode",
            ];
            const cat = categories[catIndex];
            if (cat) handleMentionCategorySelect(cat);
          }
        } else {
          const item = mentionResults[mention.selectedIndex];
          if (item) handleMentionSelect(item);
        }
        break;
      }

      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        if (mention.activeCategory) {
          handleMentionCategoryBack();
        } else {
          setMention(MENTION_INITIAL);
        }
        break;

      case "Tab":
        e.preventDefault();
        setMention(MENTION_INITIAL);
        break;
    }
  }, [mention, mentionResults, handleMentionSelect, handleMentionCategorySelect, handleMentionCategoryBack, handleAgentSearch]);

  const handleSelect = useCallback(
    (entityId: string, schemaId: string) => {
      setQuery("");
      setFocused(false);
      setTrackedMentions([]);
      inputRef.current?.blur();
      // Drill into the entity via the module's registered navigation
      // hook. Falls back to deriving entityType from schemaId
      // ("contacts.person" → "person") when the module didn't provide
      // a custom navigateToEntity. See contributions.ts:97.
      runtime.agent.navigateToEntity(schemaId, entityId, { id: entityId }, runtime, navigate);
    },
    [navigate, runtime],
  );

  const showDropdown = focused && query.trim().length > 0 && !mention.active;

  return (
    <div
      data-tauri-drag-region
      className="relative flex items-center h-[46px] shrink-0 bg-surface-tertiary border-b border-edge select-none"
    >
      {/* Left — logo (web only) or traffic lights spacer (Tauri) */}
      <div className={`shrink-0 flex items-center justify-center ${isTauri ? "w-[78px]" : "w-[60px]"}`} data-tauri-drag-region>
        {!isTauri && (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="206 231 545 499" className="h-[18px]" aria-label="Magnis">
            <path d="M206 612V391C206 285 288 231 372 231C456 231 538 285 538 391V612H422V391C422 361 400 347 372 347C344 347 322 361 322 391V612Z" fill="currentColor" className="text-content-secondary" />
            <path d="M519 249A166 166 0 0 1 751 373V612H637V393A47 47 0 0 0 587 346A119 119 0 0 0 519 249Z" fill="currentColor" className="text-content-secondary" />
            <rect x="206" y="612" width="116" height="118" fill="#f1582e" />
            <rect x="422" y="612" width="116" height="118" fill="#f1582e" />
            <rect x="637" y="612" width="116" height="118" fill="#f1582e" />
          </svg>
        )}
      </div>

      {/* Center — search field (+ global needs-reply bell) or connecting status */}
      <div className="flex-1 flex items-center justify-center" data-tauri-drag-region>
        {backendReady ? (
          <div
            ref={wrapperRef}
            data-no-window-drag
            className="relative w-full max-w-[480px]"
          >
            <div
              className={`
                flex items-center gap-2 w-full
                h-[36px] px-3 rounded-lg
                bg-surface/60 border transition-colors duration-100
                ${focused ? "border-accent/50 bg-surface" : "border-edge/60 hover:border-edge"}
              `}
            >
              <Icon name="search" size={14} className="shrink-0 text-content-tertiary" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => { setFocused(true); }}
                placeholder="Search or jump to…"
                className="flex-1 bg-transparent text-[13px] text-content placeholder:text-content-tertiary
                  outline-none border-none p-0 leading-[30px]"
              />
              {!focused && !query && (
                <kbd className="shrink-0 text-[11px] text-content-tertiary/60 font-sans">⌘K</kbd>
              )}
              {isSearching && (
                <Icon name="loader" size={12} className="shrink-0 text-content-tertiary animate-spin" />
              )}
            </div>

            {/* @-mention popup — override MentionPopup's bottom-full to open downward */}
            {mention.active && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 [&>div]:static [&>div]:mb-0">
                <MentionPopup
                  results={mentionResults}
                  isLoading={mentionLoading}
                  selectedIndex={mention.selectedIndex}
                  hasQuery={mention.query.length > 0}
                  activeCategory={mention.activeCategory}
                  onSelect={handleMentionSelect}
                  onCategorySelect={handleMentionCategorySelect}
                  onCategoryBack={handleMentionCategoryBack}
                />
              </div>
            )}

            {showDropdown && (
              <ErrorBoundary
                fallback={
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-full max-w-[520px] z-50
                      rounded-xl bg-surface border border-edge shadow-xl px-4 py-3
                      text-sm text-content-tertiary"
                  >
                    Search rendering crashed. Type to retry.
                  </div>
                }
              >
                <SearchResults
                  results={results}
                  isSearching={isSearching}
                  query={query}
                  onSelect={handleSelect}
                  onAgentSearch={handleAgentSearch}
                />
              </ErrorBoundary>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[13px] text-content-tertiary animate-pulse">
            <Icon name="loader" size={14} className="animate-spin" />
            <span>Connecting to backend…</span>
          </div>
        )}
        {backendReady && (
          /* Global bell — attached right of the search row. The relative
             wrapper anchors NeedsReplyPopover; a mousedown inside it (the
             bell) is "inside the anchor" for the popover's outside-close
             check, so the toggle doesn't close-then-reopen. */
          <div className="relative ml-1.5 shrink-0" data-no-window-drag>
            <NotificationBell
              count={needsReplyItems.length}
              onClick={() => { setBellOpen((v) => !v); }}
            />
            {bellOpen && (
              <NeedsReplyPopover
                items={needsReplyItems}
                onSelect={handleBellSelect}
                onClose={() => { setBellOpen(false); }}
              />
            )}
          </div>
        )}
      </div>

      {/* Right — toggle buttons */}
      <div className="w-[100px] shrink-0 flex items-center justify-center gap-1" data-tauri-drag-region>
        <button
          type="button"
          data-no-window-drag
          onClick={toggleStatusBar}
          className={`flex items-center justify-center w-[28px] h-[28px] rounded-md
            hover:text-content hover:bg-surface-hover/50
            transition-colors duration-100 border-none bg-transparent cursor-pointer
            ${statusBarVisible ? "text-content-tertiary" : "text-content-tertiary/50"}`}
          title="Toggle status bar"
          aria-label="Toggle status bar"
        >
          <Icon name="panel-bottom" size={18} />
        </button>
        <button
          type="button"
          data-no-window-drag
          onClick={toggleAgentPanel}
          className={`flex items-center justify-center w-[28px] h-[28px] rounded-md
            hover:text-content hover:bg-surface-hover/50
            transition-colors duration-100 border-none bg-transparent cursor-pointer
            ${agentPanelVisible ? "text-content-tertiary" : "text-content-tertiary/50"}`}
          title="Toggle agent panel"
          aria-label="Toggle agent panel"
        >
          <Icon name="panel-right" size={18} />
        </button>
      </div>
    </div>
  );
}
